import type { Evento, ResumenEscaneos, ResumenInvitados, ResumenVentas } from '../../api/ops/pedidos';
import { cifra } from '../../logica/fechasCortas';
import { CANAL } from './textos';

/**
 * Cuentas del informe del evento (10.8). Solo **cuentan entradas y personas** con lo que da la API (sumar las ventas de
 * cada canal, repartir el aforo en partes); el dinero (ingresos, reembolsado) se pinta tal cual llega (`revenue`,
 * `refunded`). Sin variación «frente al…»: la API no la da (G290).
 */
export type Momento = 'antes' | 'en-vivo' | 'terminado';

/** En vivo desde la apertura de puertas (o el inicio) hasta el fin; terminado si ya pasó o está `ended`. */
export function momentoDe(e: Pick<Evento, 'status' | 'starts_at' | 'ends_at' | 'door_opens_at'>, ahora: Date): Momento {
    if (e.status === 'ended') {
        return 'terminado';
    }
    const desde = Date.parse(e.door_opens_at ?? e.starts_at ?? '');
    const hasta = Date.parse(e.ends_at ?? '');
    const t = ahora.getTime();
    if (!Number.isNaN(hasta) && t >= hasta) {
        return 'terminado';
    }
    if (!Number.isNaN(desde) && t >= desde) {
        return 'en-vivo';
    }
    return 'antes';
}

/** El evento que se abre: el pedido (`?evento=`), el que está en vivo, el próximo o el último que terminó. */
export function eventoInicial(eventos: readonly Evento[], pedido: string | null, ahora: Date): Evento | undefined {
    const visibles = eventos.filter((e) => e.status === 'published' || e.status === 'ended');
    const pedidoEv = eventos.find((e) => e.id === pedido);
    if (pedidoEv !== undefined) {
        return pedidoEv;
    }
    const vivo = visibles.find((e) => momentoDe(e, ahora) === 'en-vivo');
    if (vivo !== undefined) {
        return vivo;
    }
    const inicio = (e: Evento) => Date.parse(e.starts_at ?? '') || 0;
    const proximos = visibles.filter((e) => momentoDe(e, ahora) === 'antes').sort((a, b) => inicio(a) - inicio(b));
    if (proximos[0] !== undefined) {
        return proximos[0];
    }
    return [...visibles].sort((a, b) => inicio(b) - inicio(a))[0];
}

export interface Parte {
    clave: string;
    etiqueta: string;
    valor: number;
    color: string;
}

/** El aforo en partes (barra apilada): vendidas, pendientes de pago, invitados, cupo de taquilla y libres. */
export function partesDelAforo(c: ResumenVentas['capacity'], terminado: boolean): Parte[] {
    return [
        { clave: 'vendidas', etiqueta: 'Vendidas', valor: c.tickets_confirmed, color: 'var(--rv-ink)' },
        { clave: 'pendientes', etiqueta: 'Pendientes de pago', valor: c.tickets_held, color: 'var(--rv-warning, #f2b705)' },
        { clave: 'invitados', etiqueta: 'Invitados', valor: c.guests_held, color: 'var(--rv-fg-3)' },
        { clave: 'taquilla', etiqueta: 'Cupo de taquilla', valor: c.box_office_reserved, color: 'var(--rv-fg-4)' },
        { clave: 'libres', etiqueta: terminado ? 'Sin vender' : 'Libres', valor: c.available, color: 'var(--rv-rule)' },
    ];
}

export interface Barra {
    clave: string;
    etiqueta: string;
    valor: number;
}

/** Ventas por tipo, con el nombre de cada tipo si se conoce. */
export function ventasPorTipo(r: ResumenVentas, nombres: Readonly<Record<string, string>>): Barra[] {
    return r.by_ticket_type.map((t, i) => ({ clave: t.ticket_type_id, etiqueta: nombres[t.ticket_type_id] ?? `Tipo ${String(i + 1)}`, valor: t.sold }));
}

/** Ventas por canal (mapa común de canales, G510, G540): la suma de cada canal en todos los tipos. */
export function ventasPorCanal(r: ResumenVentas): Barra[] {
    const suma = new Map<string, number>();
    for (const t of r.by_ticket_type) {
        for (const [canal, n] of Object.entries(t.by_channel ?? {})) {
            suma.set(canal, (suma.get(canal) ?? 0) + n);
        }
    }
    return [...suma.entries()]
        .filter(([, n]) => n > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([canal, n]) => ({ clave: canal, etiqueta: canal in CANAL ? CANAL[canal as keyof typeof CANAL][0] : canal, valor: n }));
}

/** Vendidas: la suma de lo vendido de cada tipo. */
export function vendidas(r: ResumenVentas): number {
    return r.by_ticket_type.reduce((n, t) => n + t.sold, 0);
}

/** Llegadas por hora: «21:00» y cuántas; la hora en curso, marcada. */
export function llegadasPorHora(s: ResumenEscaneos, ahora: Date): (Barra & { enCurso: boolean })[] {
    return s.by_hour.map((h) => {
        const d = new Date(h.hour);
        const etiqueta = `${String(d.getHours()).padStart(2, '0')}:00`;
        const enCurso = ahora.getTime() >= d.getTime() && ahora.getTime() < d.getTime() + 3_600_000;
        return { clave: h.hour, etiqueta, valor: h.arrivals, enCurso };
    });
}

/** Porcentaje entero (0 si no hay base). */
export function porcentaje(n: number, de: number): number {
    return de <= 0 ? 0 : Math.round((n / de) * 100);
}

/** «12 por Instagram y 30 por email. 3 de Instagram aún no han pedido su pase.» */
export function pieInvitados(s: ResumenInvitados): string {
    const canales = [s.by_channel?.instagram ? `${cifra(s.by_channel.instagram)} por Instagram` : '', s.by_channel?.email ? `${cifra(s.by_channel.email)} por email` : ''].filter((x) => x !== '').join(' y ');
    const sin = s.personal_links_unused ?? 0;
    return [canales === '' ? '' : `${canales}.`, sin > 0 ? `${cifra(sin)} de Instagram aún no han pedido su pase.` : ''].filter((x) => x !== '').join(' ');
}
